import type { BaseType, SourceType, TableType } from 'nocodb-sdk'
import { ProjectRoles, SqlUiFactory } from 'nocodb-sdk'
import { isString } from '@vue/shared'
import { acceptHMRUpdate, defineStore } from 'pinia'
import type Record from '~icons/nc-icons/record'

export const useBase = defineStore('baseStore', () => {
  const { $e } = useNuxtApp()

  const { api, isLoading } = useApi()

  const router = useRouter()

  const route = router.currentRoute

  const { loadRoles } = useRoles()

  const { refreshCommandPalette } = useCommandPalette()

  const forcedProjectId = ref<string>()

  const baseId = computed(() => forcedProjectId.value || (route.value.params.baseId as string))

  const basesStore = useBases()

  const workspaceStore = useWorkspace()

  const tablesStore = useTablesStore()

  const scriptStore = useScriptStore()
  const dashboardStore = useDashboardStore()
  const workflowStore = useWorkflowStore()

  const { loadScripts } = scriptStore
  const { loadDashboards } = dashboardStore
  const { loadWorkflows } = workflowStore

  const { isEEFeatureBlocked, blockPrivateBases } = useEeConfig()

  // todo: refactor
  const sharedProject = ref<BaseType>()

  const openedProject = computed(() => basesStore.bases.get(baseId.value))

  // todo: new-layout
  const base = computed<NcProject>(() => basesStore.bases.get(baseId.value) || sharedProject.value || {})
  const tables = computed<TableType[]>(() => tablesStore.baseTables.get(baseId.value) || [])

  const baseLoadedHook = createEventHook<BaseType>()

  const sources = computed<SourceType[]>(() => base.value?.sources || [])

  const baseMetaInfo = ref<ProjectMetaInfo | undefined>()

  const lastOpenedViewMap = ref<Record<string, string>>({})

  const managedApp = ref<any>(null)

  const managedAppVersions = ref<any[]>([])

  const managedAppVersionsInfo = computed(() => {
    const result = {
      current: managedAppVersions.value.find((v: any) => v.id === base.value.managed_app_version_id),
      published: managedAppVersions.value.find((v: any) => v.status === 'published'),
      draft: managedAppVersions.value.find((v: any) => v.status === 'draft'),
      updateAvailable: false,
    }

    if (
      result.current &&
      result.published &&
      result.current.id !== result.published.id &&
      result.published.version_number > result.current.version_number
    ) {
      result.updateAvailable = true
    }

    return result
  })

  const isManagedAppMaster = computed(() => !!base.value?.managed_app_master && !!base.value?.managed_app_id)

  const isManagedAppInstaller = computed(() => !!base.value?.managed_app_id && !base.value?.managed_app_master)

  // Sandbox
  const isSandbox = computed(() => !!base.value?.is_sandbox)
  const isSandboxProduction = computed(() => !!base.value?.is_sandbox_production)

  const sandboxInfo = ref<{ id: string; production_base_id: string; sandbox_base_id: string } | null>(null)
  const sandboxList = ref<{ id: string; production_base_id: string; sandbox_base_id: string }[]>([])

  const loadSandboxInfo = async () => {
    if (!base.value?.id || !base.value?.fk_workspace_id) return

    try {
      // Fetch sandbox record using sandboxGet operation
      const response = await api.internal.getOperation(base.value.fk_workspace_id, base.value.id, {
        operation: 'sandboxGet',
      })

      if (response) {
        sandboxInfo.value = response
      }
    } catch (_e) {
      // Sandbox info not available
    }
  }

  // todo: refactor path param name and variable name
  const baseType = computed(() => route.value.params.typeOrId as string)

  const idUserMap = computed(() => {
    return (basesStore.basesUser.get(baseId.value) || []).reduce((acc, user) => {
      acc[user.id] = user
      acc[user.email] = user
      return acc
    }, {} as Record<string, any>)
  })

  const baseMeta = computed<Record<string, any>>(() => {
    const defaultMeta = {
      showNullAndEmptyInFilter: false,
    }
    try {
      return (isString(base.value.meta) ? JSON.parse(base.value.meta) : base.value.meta) ?? defaultMeta
    } catch (e) {
      return defaultMeta
    }
  })

  const isPrivateBase = computed(() => base.value.default_role === ProjectRoles.NO_ACCESS)

  const showBaseAccessRequestOverlay = computed(() => {
    return blockPrivateBases.value && isPrivateBase.value
  })

  const sqlUis = computed(() => {
    const temp: Record<string, any> = {}
    for (const source of sources.value) {
      if (source.id) {
        temp[source.id] = SqlUiFactory.create({ client: source.type })
      }
    }
    return temp
  })

  /**
   * @Note - Always use this fn inside computed as `sqlUis` is computed property
   */
  function getSqlUiBySourceId(sourceId?: string): any {
    if (sourceId && sqlUis.value[sourceId]) {
      return sqlUis.value[sourceId]
    }

    return Object.values(sqlUis.value)[0]
  }

  function getBaseType(sourceId?: string) {
    return sources.value.find((source) => source.id === sourceId)?.type || ClientType.MYSQL
  }

  function isMysql(sourceId?: string) {
    return ['mysql', ClientType.MYSQL].includes(getBaseType(sourceId))
  }

  function isSqlite(sourceId?: string) {
    return getBaseType(sourceId) === ClientType.SQLITE
  }

  function isPg(sourceId?: string) {
    return getBaseType(sourceId) === 'pg'
  }

  function isSnowflake(sourceId?: string) {
    return getBaseType(sourceId) === 'snowflake'
  }

  function isDatabricks(sourceId?: string) {
    return getBaseType(sourceId) === 'databricks'
  }

  function isXcdbBase(sourceId?: string) {
    const source = sources.value.find((source) => source.id === sourceId)
    return (source?.is_meta as boolean) || (source?.is_local as boolean) || false
  }

  const isSharedBase = computed(() => baseType.value === 'base')

  const isSharedErd = computed(() => baseType.value === 'ERD')

  async function loadProjectMetaInfo(force?: boolean) {
    if (!baseMetaInfo.value || force) {
      baseMetaInfo.value = await api.base.metaGet(base.value.id!, {})
    }
  }

  // todo: add force parameter
  async function loadTables() {
    if (base.value.id) {
      const promises: Promise<any>[] = [tablesStore.loadProjectTables(base.value.id, true)]

      // Only load EE features (scripts, dashboards, workflows) when licensed
      if (!isEEFeatureBlocked.value) {
        promises.push(
          loadScripts({ baseId: base.value.id || baseId.value }),
          loadDashboards({ baseId: base.value.id || baseId.value }),
          loadWorkflows({ baseId: base.value.id || baseId.value }),
        )
      }

      await Promise.allSettled(promises)

      // tables.value = basesStore.baseTableList[base.value.id]
      //   await api.dbTable.list(base.value.id, {
      //   includeM2M: includeM2M.value,
      // })

      // if (tablesResponse.list) {
      //   tables.value = tablesResponse.list
      // }
    }
  }

  async function loadProject(_withTheme = true, forcedId?: string) {
    if (forcedId) forcedProjectId.value = forcedId
    if (baseType.value === 'base') {
      try {
        const baseData = await api.public.sharedBaseGet(route.value.params.baseId as string)

        forcedProjectId.value = baseData.base_id
        sharedProject.value = await api.base.read(baseData.base_id!)
      } catch (e: any) {
        if (e?.response?.status === 404) {
          return router.push('/error/404')
        }
        throw e
      }
    } else if (baseId.value) {
      await basesStore.loadProject(baseId.value)
      // base.value = basesStore.bases[baseId.value] // await api.base.read(baseId.value)
    } else {
      console.warn('Base id not found')
      return
    }

    if (isSharedBase.value) {
      await loadRoles(base.value.id || baseId.value, {
        isSharedBase: isSharedBase.value,
        sharedBaseId: route.value.params.baseId as string,
      })
    } else if (isSharedErd.value) {
      await loadRoles(base.value.id || baseId.value, {
        isSharedErd: isSharedErd.value,
        sharedErdId: route.value.params.erdUuid as string,
      })
    } else {
      await loadRoles(base.value.id || baseId.value)
    }

    await loadTables()

    if (!isSharedBase.value) {
      await basesStore.getBaseUsers({
        baseId: base.value.id || baseId.value,
      })

      basesStore
        .getBaseTeams({
          baseId: base.value.id || baseId.value,
        })
        .catch(() => {
          // ignore
        })
    }

    if (base.value?.is_sandbox || base.value?.is_sandbox_production) {
      await loadSandboxInfo()
    }

    // if (withTheme) setTheme(baseMeta.value?.theme)

    return baseLoadedHook.trigger(base.value)
  }

  async function updateProject(data: Partial<BaseType>) {
    if (baseType.value === 'base') {
      return
    }

    if (data.meta && typeof data.meta === 'string') {
      await api.base.update(baseId.value, data)
    } else {
      await api.base.update(baseId.value, { ...data, meta: stringifyProp(data.meta) })
    }

    refreshCommandPalette()
  }

  async function saveTheme(_theme: Partial<ThemeConfig>) {
    const fullTheme = {
      primaryColor: theme.value.primaryColor,
      accentColor: theme.value.accentColor,
      ..._theme,
    }

    await updateProject({
      color: fullTheme.primaryColor,
      meta: {
        ...baseMeta.value,
        theme: fullTheme,
      },
    })

    // setTheme(fullTheme)

    $e('c:themes:change')
  }

  async function hasEmptyOrNullFilters() {
    return await api.base.hasEmptyOrNullFilters(baseId.value)
  }

  const reset = () => {
    // base.value = {}
    // tables.value = []
    baseMetaInfo.value = undefined
    // setTheme()
  }

  const setProject = (baseVal: BaseType) => {
    sharedProject.value = baseVal
  }

  const baseUrl = ({
    id,
    type: _type,
    isSharedBase,
    projectPage,
  }: {
    id: string
    type: 'database' | 'documentation'
    isSharedBase?: boolean
    projectPage?: ProjectPageType
  }) => {
    if (isSharedBase) {
      const typeOrId = route.value.params.typeOrId as string
      const baseId = route.value.params.baseId as string

      return `/${typeOrId}/${baseId}`
    }

    const workspaceId = workspaceStore.activeWorkspaceId
    const basUrl = `/${workspaceId}/${id}`

    if (projectPage) {
      const slug = baseSettingsTabToSlug[projectPage] || projectPage
      return `${basUrl}/settings/${slug}`
    }

    return basUrl
  }

  // Load managed app info and current version
  const loadManagedApp = async () => {
    if (!base.value?.managed_app_id || !base.value?.managed_app_master || !base.value?.fk_workspace_id) return

    try {
      const response = await api.internal.getOperation(base.value.fk_workspace_id, base.value.id!, {
        operation: 'managedAppGet',
        baseId: base.value.id,
      } as any)
      if (response) {
        managedApp.value = response
      }
    } catch (e) {
      console.error('Failed to load managed app:', e)
    }
  }

  // Load current version info
  const loadCurrentVersion = async () => {
    if (!base.value?.managed_app_version_id || !base.value?.fk_workspace_id) return

    try {
      // Get version details from versions list
      const response = await api.internal.getOperation(base.value.fk_workspace_id, base.value.id!, {
        operation: 'managedAppVersionsList',
        managedAppId: (base.value as any).managed_app_id,
      } as any)
      if (response?.list) {
        managedAppVersions.value = response.list
      }
    } catch (e) {
      console.error('Failed to load current version:', e)
    }
  }

  const isManualUpdating = ref(false)

  const triggerManualUpdate = async () => {
    if (!base.value?.fk_workspace_id || !base.value?.id) return

    isManualUpdating.value = true
    try {
      await api.internal.postOperation(
        base.value.fk_workspace_id,
        base.value.id,
        { operation: 'managedAppManualUpdate' } as any,
        {
          baseId: base.value.id,
        },
      )
      message.success(getI18n().global.t('msg.success.updated'))
      await loadCurrentVersion()
    } catch (error: any) {
      message.error(await extractSdkResponseErrorMsg(error))
    } finally {
      isManualUpdating.value = false
    }
  }

  watch(
    () => route.value.params.baseType,
    (n) => {
      if (!n) reset()
    },
    { immediate: true },
  )

  watch(
    () => openedProject.value?.id,
    () => {
      if (!openedProject.value) return

      if (openedProject.value.isExpanded) return

      openedProject.value.isExpanded = true
    },
  )

  const navigateToProjectPage = async ({
    page,
    action,
  }: {
    page: 'overview' | 'collaborator' | 'data-source' | 'permissions'
    action?: string
  }) => {
    const wsId = route.value.params.typeOrId
    const bId = route.value.params.baseId
    const slug = baseSettingsTabToSlug[page] || page
    const query = action ? { action } : undefined

    navigateTo({ path: `/${wsId}/${bId}/settings/${slug}`, query })
  }

  return {
    base,
    sources,
    tables,
    baseId,
    loadProject,
    updateProject,
    loadTables,
    isMysql,
    isPg,
    isSqlite,
    isSnowflake,
    isDatabricks,
    sqlUis,
    getSqlUiBySourceId,
    isSharedBase,
    isSharedErd,
    loadProjectMetaInfo,
    baseMetaInfo,
    baseMeta,
    saveTheme,
    baseLoadedHook: baseLoadedHook.on,
    reset,
    isLoading,
    lastOpenedViewMap,
    isXcdbBase,
    hasEmptyOrNullFilters,
    setProject,
    baseUrl,
    getBaseType,
    navigateToProjectPage,
    forcedProjectId,
    idUserMap,
    isPrivateBase,
    showBaseAccessRequestOverlay,
    isManagedAppMaster,
    isManagedAppInstaller,
    managedApp,
    loadManagedApp,
    loadCurrentVersion,
    managedAppVersions,
    managedAppVersionsInfo,
    isManualUpdating,
    triggerManualUpdate,
    // Sandbox
    isSandbox,
    isSandboxProduction,
    sandboxInfo,
    sandboxList,
    loadSandboxInfo,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useBase as any, import.meta.hot))
}
